import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff, Trash2, Pencil, Upload, MoreVertical, X } from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const Card = ({ title, children }) => (
  <div className="bg-[#1B3352] border border-white/10 rounded-2xl p-8 mb-6">
    <h2 className="text-white text-lg font-medium mb-5">{title}</h2>
    {children}
  </div>
);

const SaveBtn = ({ loading, testId, children = "Save" }) => (
  <button type="submit" disabled={loading} data-testid={testId}
    className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60 flex items-center gap-2">
    {loading ? <Loader2 size={16} className="animate-spin" /> : children}
  </button>
);

const emptyPatient = {
  mobile: "", name: "", age: "", sex: "", address: "", city: "", state: "", referred_by: "",
  date: new Date().toISOString().slice(0, 10), follow_up: "",
  pulse: "", blood_pressure: "", height: "", weight: "", bmi: "",
};

const PatientForm = ({ token, onSaved, editingPatient, visitMode, clearEditing, allPatients, onLoadExisting }) => {
  const [form, setForm] = useState(emptyPatient);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (editingPatient) {
      if (visitMode) {
        setForm({
          ...emptyPatient,
          ...editingPatient,
          date: new Date().toISOString().slice(0, 10),
          pulse: "", blood_pressure: "", height: "", weight: "", bmi: "",
        });
      } else {
        setForm({ ...emptyPatient, ...editingPatient });
      }
    } else {
      setForm(emptyPatient);
    }
    setMatches([]);
  }, [editingPatient, visitMode]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkExisting = (name, mobile) => {
    if (editingPatient) { setMatches([]); return; }
    const n = name.trim().toLowerCase();
    const m = mobile.trim();
    if (!n && !m) { setMatches([]); return; }
    const found = allPatients.filter((p) =>
      (m && p.mobile && p.mobile.includes(m) && m.length >= 4) ||
      (n && p.name.toLowerCase().includes(n) && n.length >= 3)
    ).slice(0, 5);
    setMatches(found);
  };

  const onNameChange = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, name: val }));
    checkExisting(val, form.mobile);
  };

  const onMobileChange = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, mobile: val }));
    checkExisting(form.name, val);
  };

  const loadExisting = (p) => {
    onLoadExisting(p);
    setMatches([]);
    toast.success(`Loaded ${p.name} — vitals cleared for today's visit`);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Patient name is required"); return; }
    setSaving(true);
    try {
      if (editingPatient) {
        await axios.put(`${API}/patients/${editingPatient.patient_id}`, form, authHeader(token));
        toast.success("Patient updated");
      } else {
        await axios.post(`${API}/patients`, form, authHeader(token));
        toast.success("Patient saved");
      }
      setForm(emptyPatient);
      clearEditing();
      onSaved();
    } catch (err) {
      toast.error("Failed to save patient");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={editingPatient ? (visitMode ? `New Visit — ${editingPatient.name}` : `Edit Patient — ${editingPatient.name}`) : "Add / Update Patient"}>
      {matches.length > 0 && (
        <div className="mb-5 bg-[#14263D] border border-[#5ECCA0]/40 rounded-lg p-4">
          <div className="text-[#5ECCA0] text-xs uppercase font-mono-ui mb-2">Existing patient found — returning visit?</div>
          {matches.map((p) => (
            <button key={p.patient_id} type="button" onClick={() => loadExisting(p)}
              data-testid={`reception-existing-match-${p.patient_id}`}
              className="block w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-sm">
              <span className="text-white font-medium">{p.name}</span>
              <span className="text-white/50 ml-2">#{p.patient_id} · {p.mobile}</span>
              <span className="text-white/40 ml-2">· Registered {p.date}{p.follow_up ? ` · Follow up ${p.follow_up}` : ""}</span>
            </button>
          ))}
        </div>
      )}
      <form onSubmit={save}>
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Mobile</Label>
            <Input value={form.mobile} onChange={onMobileChange} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-mobile-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Patient Name</Label>
            <Input value={form.name} onChange={onNameChange} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-name-input" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 mb-5">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Age</Label>
            <Input value={form.age} onChange={set("age")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-age-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Sex</Label>
            <select value={form.sex} onChange={set("sex")} data-testid="reception-sex-select"
              className="mt-2 w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-2.5 text-sm">
              <option value="">Select Sex</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Referred By</Label>
            <Input value={form.referred_by} onChange={set("referred_by")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-referred-input" />
          </div>
        </div>
        <div className="mb-5">
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Address</Label>
          <Input value={form.address} onChange={set("address")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-address-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">City</Label>
            <Input value={form.city} onChange={set("city")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-city-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">State</Label>
            <Input value={form.state} onChange={set("state")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-state-input" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Date</Label>
            <Input type="date" value={form.date} onChange={set("date")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-date-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Follow Up</Label>
            <Input type="date" value={form.follow_up} onChange={set("follow_up")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-followup-input" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-5 mb-6">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Pulse (/min)</Label>
            <Input value={form.pulse} onChange={set("pulse")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-pulse-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">BP (mm Hg)</Label>
            <Input value={form.blood_pressure} onChange={set("blood_pressure")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-bp-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Height (cm)</Label>
            <Input value={form.height} onChange={set("height")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-height-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Weight (kg)</Label>
            <Input value={form.weight} onChange={set("weight")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-weight-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">BMI</Label>
            <Input value={form.bmi} onChange={set("bmi")} className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="reception-bmi-input" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SaveBtn loading={saving} testId="reception-save-patient-btn">{editingPatient ? "Update Patient" : "Save Patient"}</SaveBtn>
          {editingPatient && (
            <button type="button" onClick={() => { setForm(emptyPatient); clearEditing(); }}
              className="text-white/50 hover:text-white text-sm">Cancel edit</button>
          )}
        </div>
      </form>
    </Card>
  );
};

const ViewPatientModal = ({ patient, onClose }) => {
  if (!patient) return null;
  const Row = ({ label, value }) => (
    <div className="text-white/80"><span className="text-white/40">{label}:</span> {value || "-"}</div>
  );
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#1B3352] border border-white/10 rounded-2xl p-8 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white text-lg font-medium">{patient.name}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="reception-view-close-btn"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Row label="Patient ID" value={`#${patient.patient_id}`} />
          <Row label="Mobile" value={patient.mobile} />
          <Row label="Age" value={patient.age} />
          <Row label="Sex" value={patient.sex} />
          <div className="text-white/80 sm:col-span-2"><span className="text-white/40">Address:</span> {patient.address || "-"}</div>
          <Row label="City" value={patient.city} />
          <Row label="State" value={patient.state} />
          <Row label="Referred By" value={patient.referred_by} />
          <Row label="Date" value={patient.date} />
          <Row label="Follow Up" value={patient.follow_up} />
          <Row label="Pulse" value={patient.pulse} />
          <Row label="BP" value={patient.blood_pressure} />
          <Row label="Height" value={patient.height} />
          <Row label="Weight" value={patient.weight} />
          <Row label="BMI" value={patient.bmi} />
        </div>
      </div>
    </div>
  );
};

const PatientList = ({ items, onEdit, onView }) => {
  const [openMenuId, setOpenMenuId] = useState(null);

  return (
    <Card title="Patient List">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="text-[#5ECCA0] text-xs uppercase font-mono-ui border-b border-white/10">
              <th className="py-2 pr-4">Patient ID</th>
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Mobile</th>
              <th className="py-2 pr-4">Date</th>
              <th className="py-2 pr-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.patient_id} className="border-b border-white/5 text-white/80" data-testid={`reception-patient-row-${p.patient_id}`}>
                <td className="py-2 pr-4">{p.patient_id}</td>
                <td className="py-2 pr-4">{p.name}</td>
                <td className="py-2 pr-4">{p.mobile}</td>
                <td className="py-2 pr-4">{p.date}</td>
                <td className="py-2 pr-4 relative">
                  <button onClick={() => setOpenMenuId(openMenuId === p.patient_id ? null : p.patient_id)}
                    data-testid={`reception-menu-btn-${p.patient_id}`}
                    className="text-white/60 hover:text-white p-1">
                    <MoreVertical size={16} />
                  </button>
                  {openMenuId === p.patient_id && (
                    <div className="absolute right-0 z-20 mt-1 bg-[#14263D] border border-white/15 rounded-lg shadow-lg overflow-hidden w-28">
                      <button onClick={() => { onView(p); setOpenMenuId(null); }}
                        data-testid={`reception-view-btn-${p.patient_id}`}
                        className="block w-full text-left px-4 py-2 text-sm text-white/80 hover:bg-white/10">
                        View
                      </button>
                      <button onClick={() => { onEdit(p); setOpenMenuId(null); }}
                        data-testid={`reception-edit-btn-${p.patient_id}`}
                        className="block w-full text-left px-4 py-2 text-sm text-white/80 hover:bg-white/10">
                        Edit
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-white/40">No patients yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const emptyMedicine = { name: "", strength: "", composition: "", remarks: "" };

const MedicineMasterForm = ({ token }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyMedicine);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [linkUploading, setLinkUploading] = useState(false);
  const [sheetLink, setSheetLink] = useState("");

  const load = () => {
    axios.get(`${API}/medicines`, authHeader(token)).then((res) => setItems(res.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Medicine name is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/medicines/${editingId}`, form, authHeader(token));
        toast.success("Medicine updated");
      } else {
        await axios.post(`${API}/medicines`, { ...form, order: items.length }, authHeader(token));
        toast.success("Medicine added");
      }
      setForm(emptyMedicine);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error("Failed to save medicine");
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setForm({ name: item.name, strength: item.strength, composition: item.composition, remarks: item.remarks || "" });
    setEditingId(item.id);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkUploading(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const mapped = rows.map((r) => {
        const norm = {};
        Object.keys(r).forEach((k) => { norm[k.trim().toLowerCase()] = r[k]; });
        return {
          name: String(norm["medicine name"] || norm["name"] || "").trim(),
          strength: String(norm["strength"] || "").trim(),
          composition: String(norm["composition"] || "").trim(),
          remarks: String(norm["remarks"] || "").trim(),
        };
      }).filter((m) => m.name);
      if (mapped.length === 0) { toast.error("No valid rows found — check column headers"); return; }
      await axios.post(`${API}/medicines/bulk-import`, mapped, authHeader(token));
      toast.success(`Imported ${mapped.length} medicines`);
      load();
    } catch (err) {
      toast.error("Failed to import — check the file format");
    } finally {
      setBulkUploading(false);
      e.target.value = "";
    }
  };

  const handleLinkImport = async (e) => {
    e.preventDefault();
    if (!sheetLink.trim()) { toast.error("Paste a Google Sheets or Drive link first"); return; }
    setLinkUploading(true);
    try {
      const res = await axios.post(`${API}/medicines/import-from-link`, { url: sheetLink.trim() }, authHeader(token));
      toast.success(`Imported ${res.data.imported} medicines`);
      setSheetLink("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import from link");
    } finally {
      setLinkUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/medicines/${id}`, authHeader(token));
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  return (
    <Card title="Medicine Master Sheet">
      <div className="mb-6 pb-6 border-b border-white/10">
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui block mb-2">Bulk Import from Excel</Label>
        <p className="text-white/40 text-xs mb-3">Upload a .xlsx file with columns: Medicine Name, Strength, Composition, Remarks</p>
        <label className="inline-flex items-center gap-2 rounded-full bg-[#14263D] border border-white/15 text-white/80 px-5 py-2.5 text-sm cursor-pointer hover:border-[#5ECCA0]/50">
          <Upload size={15} />
          {bulkUploading ? "Importing..." : "Upload Excel File"}
          <input type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" data-testid="medicine-bulk-upload-input" disabled={bulkUploading} />
        </label>

        <div className="mt-4">
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui block mb-2">Or Import from Google Sheets / Drive Link</Label>
          <p className="text-white/40 text-xs mb-3">Sheet must be shared as "Anyone with the link can view"</p>
          <form onSubmit={handleLinkImport} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
            <Input value={sheetLink} onChange={(e) => setSheetLink(e.target.value)}
              placeholder="Paste Google Sheets or Drive share link"
              className="bg-[#14263D] border-white/15 text-white flex-1" data-testid="medicine-sheet-link-input" />
            <button type="submit" disabled={linkUploading} data-testid="medicine-sheet-link-import-btn"
              className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60 whitespace-nowrap">
              {linkUploading ? "Importing..." : "Import from Link"}
            </button>
          </form>
        </div>
      </div>
      <form onSubmit={save} className="mb-8 pb-8 border-b border-white/10">
        <div className="grid sm:grid-cols-4 gap-5 mb-4">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Medicine Name</Label>
            <Input value={form.name} onChange={set("name")} placeholder="e.g. Gabapentin"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="medicine-name-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Strength</Label>
            <Input value={form.strength} onChange={set("strength")} placeholder="e.g. 300mg"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="medicine-strength-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Composition</Label>
            <Input value={form.composition} onChange={set("composition")} placeholder="e.g. Gabapentin"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="medicine-composition-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Remarks</Label>
            <Input value={form.remarks} onChange={set("remarks")} placeholder="e.g. AFTER FOOD"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="medicine-remarks-input" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SaveBtn loading={saving} testId="medicine-save-btn">{editingId ? "Update Medicine" : "Add Medicine"}</SaveBtn>
          {editingId && (
            <button type="button" onClick={() => { setForm(emptyMedicine); setEditingId(null); }}
              className="text-white/50 hover:text-white text-sm">Cancel edit</button>
          )}
        </div>
      </form>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="text-[#5ECCA0] text-xs uppercase font-mono-ui border-b border-white/10">
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Strength</th>
              <th className="py-2 pr-4">Composition</th>
              <th className="py-2 pr-4">Remarks</th>
              <th className="py-2 pr-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-b border-white/5 text-white/80">
                <td className="py-2 pr-4">{m.name}</td>
                <td className="py-2 pr-4">{m.strength}</td>
                <td className="py-2 pr-4">{m.composition}</td>
                <td className="py-2 pr-4">{m.remarks}</td>
                <td className="py-2 pr-4 flex items-center gap-3">
                  <button onClick={() => edit(m)} className="text-[#5ECCA0] hover:underline text-sm">Edit</button>
                  <button onClick={() => remove(m.id)} className="text-red-400 hover:underline text-sm">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-white/40">No medicines added yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const TABS = [
  { id: "patients", label: "Patients" },
  { id: "medicines", label: "Medicine Master" },
];

const Reception = () => {
  const [token, setToken] = useState(localStorage.getItem("reception_token") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("patients");
  const [editingPatient, setEditingPatient] = useState(null);
  const [visitMode, setVisitMode] = useState(false);
  const [allPatients, setAllPatients] = useState([]);
  const [viewingPatient, setViewingPatient] = useState(null);

  const loadPatients = () => {
    axios.get(`${API}/patients`, authHeader(token)).then((res) => setAllPatients(res.data)).catch(() => {});
  };

  useEffect(() => { if (token) loadPatients(); }, [token]); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reception/login`, { password });
      setToken(res.data.token);
      localStorage.setItem("reception_token", res.data.token);
      toast.success("Logged in");
    } catch (err) {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("reception_token");
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#14263D] flex items-center justify-center p-6">
        <form onSubmit={login} className="bg-[#1B3352] border border-white/10 rounded-2xl p-8 w-full max-w-sm">
          <h1 className="text-white text-xl font-medium mb-6">Reception Login</h1>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Password</Label>
          <div className="relative mt-2 mb-5">
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#14263D] border-white/15 text-white pr-10"
              data-testid="reception-password-input"
            />
            <button type="button" onClick={() => setShowPassword((v) => !v)} tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
              data-testid="reception-password-toggle-btn">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="reception-login-btn"
            className="w-full rounded-full bg-[#5ECCA0] text-[#14263D] font-medium py-3 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Log In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#14263D] p-6 md:p-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-white text-2xl font-medium">Reception Dashboard</h1>
          <button onClick={logout} data-testid="reception-logout-btn" className="text-white/50 hover:text-white text-sm">
            Log out
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`reception-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === t.id
                  ? "bg-[#5ECCA0] text-[#14263D]"
                  : "bg-[#1B3352] text-white/60 hover:text-white border border-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "patients" && (
          <>
            <PatientForm
              token={token}
              editingPatient={editingPatient}
              visitMode={visitMode}
              clearEditing={() => { setEditingPatient(null); setVisitMode(false); }}
              onSaved={loadPatients}
              allPatients={allPatients}
              onLoadExisting={(p) => { setEditingPatient(p); setVisitMode(true); }}
            />
            <PatientList
              items={allPatients}
              onEdit={(p) => { setEditingPatient(p); setVisitMode(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              onView={setViewingPatient}
            />
          </>
        )}
        {activeTab === "medicines" && <MedicineMasterForm token={token} />}
      </div>
      <ViewPatientModal patient={viewingPatient} onClose={() => setViewingPatient(null)} />
    </div>
  );
};

export default Reception;
